import type { Comparison, TimeGrain } from '@aibi/contracts';
import { isTemporalDimension, type SemanticModelIndex } from '@aibi/semantic-model';
import { DiagnosticBag, type AnalysisDiagnostic } from './diagnostics.js';
import { grainSupportsType } from './field-types.js';
import type {
  PlannedComparison,
  PlannedComparisonOutput,
  PlannedDimension,
  PlannedFilter,
} from './query-plan.js';

export interface ComparisonContext {
  readonly dimensions: readonly PlannedDimension[];
  readonly measureKeys: readonly string[];
  readonly filters: readonly PlannedFilter[];
  /** Claves ya usadas por el analisis (para evitar colisiones de columnas). */
  readonly usedKeys: ReadonlySet<string>;
}

export interface ComparisonResolution {
  readonly comparison: PlannedComparison | null;
  readonly diagnostics: readonly AnalysisDiagnostic[];
}

const OUTPUT_SUFFIX: Readonly<Record<Comparison['output'], string>> = {
  value: 'prev',
  difference: 'diff',
  'percent-change': 'pct',
};
const MAX_KEY_LENGTH = 64;

/**
 * Representa la comparacion temporal como nodo estructurado del plan. El AST no tiene
 * funciones de desplazamiento de fechas: el Query Engine materializa el periodo
 * comparado a partir de `offset`. Lo que no puede expresarse se reporta como `unsupported`.
 *
 * @example
 * resolveComparison(state.comparison, { dimensions, measureKeys, filters, usedKeys }, index);
 */
export function resolveComparison(
  comparison: Comparison | null,
  context: ComparisonContext,
  index: SemanticModelIndex,
): ComparisonResolution {
  const bag = new DiagnosticBag();
  const done = (result: PlannedComparison | null): ComparisonResolution => ({
    comparison: bag.hasErrors ? null : result,
    diagnostics: bag.items,
  });
  if (!comparison) return done(null);

  const dateDimensionId = comparison.dateDimensionId ?? index.model.settings.primaryDateDimensionId;
  if (dateDimensionId === undefined) {
    bag.error(
      'invalid-state',
      'Indique la dimension de fecha o defina la fecha principal del modelo.',
      { path: 'comparison.dateDimensionId' },
    );
    return done(null);
  }
  const resolved = index.resolveDimension(dateDimensionId);
  if (!resolved) {
    bag.error('missing-reference', `Dimension inexistente "${dateDimensionId}".`, {
      path: 'comparison.dateDimensionId',
    });
    return done(null);
  }
  if (!isTemporalDimension(resolved)) {
    bag.error('invalid-type', `"${resolved.dimension.name}" no es una dimension temporal.`, {
      path: 'comparison.dateDimensionId',
    });
    return done(null);
  }

  const planned = context.dimensions.find((dimension) => dimension.dimensionId === dateDimensionId);
  const grain: TimeGrain | null =
    planned?.timeGrain ??
    context.filters.reduce<TimeGrain | null>(
      (found, filter) =>
        found ??
        (filter.target.kind === 'dimension' && filter.target.dimensionId === dateDimensionId
          ? filter.target.timeGrain
          : null),
      null,
    );

  let offset: PlannedComparison['offset'] | undefined;
  switch (comparison.kind) {
    case 'previous-period':
      if (grain === null) {
        bag.error(
          'unsupported',
          'La comparacion con el periodo anterior requiere un grano temporal: incluya la dimension de fecha con timeGrain o filtre por periodo.',
          { path: 'comparison.kind' },
        );
      } else offset = { grain, periods: 1 };
      break;
    case 'same-period-previous-year':
      offset = { grain: 'year', periods: 1 };
      break;
    case 'custom-offset':
      if (!comparison.offset) {
        bag.error('invalid-state', 'Una comparacion "custom-offset" requiere offset.', {
          path: 'comparison.offset',
        });
      } else offset = { ...comparison.offset };
      break;
  }
  if (offset && !grainSupportsType(offset.grain, resolved.dataType)) {
    bag.error(
      'unsupported',
      `No se puede desplazar por "${offset.grain}" una dimension ${resolved.dataType}.`,
      { path: 'comparison.offset' },
    );
  }

  const measureKeys =
    comparison.measureKeys.length > 0 ? [...comparison.measureKeys] : [...context.measureKeys];
  if (measureKeys.length === 0) {
    bag.error('invalid-state', 'La comparacion requiere al menos una medida en el analisis.', {
      path: 'comparison.measureKeys',
    });
  }
  const outputs: PlannedComparisonOutput[] = [];
  const outputKeys = new Set<string>();
  for (const measureKey of measureKeys) {
    const key = `${measureKey}_${OUTPUT_SUFFIX[comparison.output]}`;
    if (key.length > MAX_KEY_LENGTH || context.usedKeys.has(key) || outputKeys.has(key)) {
      bag.error(
        'invalid-state',
        `No se puede generar la columna de comparacion "${key}" (colisiona o excede ${MAX_KEY_LENGTH} caracteres).`,
        { path: 'comparison.measureKeys' },
      );
      continue;
    }
    outputKeys.add(key);
    outputs.push({ key, measureKey, output: comparison.output });
  }

  if (!offset) return done(null);
  return done({
    kind: comparison.kind,
    output: comparison.output,
    dateDimensionId,
    dateKey: planned?.key ?? null,
    grain,
    offset,
    measureKeys,
    outputs,
  });
}
